// src/pages/Inventory/GoodsReceiptNoteIns_Listing.jsx
// ✅ Converted from: GoodsReceiptNoteIns_Listing.aspx / .aspx.cs
// ✅ Uses Node routes: /api/inventory/grn-ins-listing/*
// ✅ NO rights logic, NO page access control
// ✅ All logic preserved:
//    - HO/Branch radio → reloads suppliers + changes purType
//    - Project Name ↔ Project Code linked dropdowns
//    - Date validations (future date, from <= to, financial year)
//    - Status color: Confirmed=green badge, others=amber badge
//    - Confirmed rows → GRN Ins No link disabled (non-clickable)
//    - VIEW_GRN → fetches POs → passes to Entry page via sessionStorage (edit mode)
//    - GRNVEH_INS_DET → shows vehicle inspection details panel with image thumbnails
//    - Image thumbnail click → modal lightbox
//    - Site inventory toggle → shows/hides Details column
//    - Autocomplete suggestions for GRN Ins No search
//    - Royalty Number + Vehicle Code filters (passed to future SP extension)
//    - Add New → navigates to AddListing page
//    - Pagination on main grid + details grid

import { useEffect, useContext, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import LayoutWrapper from "../../components/LayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Plus,
  Search,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  Eye,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const todayISO = () => new Date().toISOString().slice(0, 10);

const GoodsReceiptNoteInsListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ── localStorage ─────────────────────────────────────────────
  const finYearId  = localStorage.getItem("finYearId")  || "";
  const branchId   = localStorage.getItem("branchId")   || "";
  const branchType = localStorage.getItem("branchType") || "HO";
  const finYearStart = localStorage.getItem("financeyearstartdate") || "";

  // ── Style helpers ─────────────────────────────────────────────
  const inputBase =
    `w-full px-3 py-2 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const thClass = `px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-white`;
  const tdClass = `px-3 py-2.5 text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`;

  // ── UI state ──────────────────────────────────────────────────
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading]         = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // ── Meta ──────────────────────────────────────────────────────
  const [isSiteInventory, setIsSiteInventory] = useState(false);

  // ── Radio (HO/Branch) ─────────────────────────────────────────
  const [rad, setRad]   = useState(branchType === "HO" ? "HO" : "BRANCH");
  const purType = rad === "HO" ? "H" : "B";

  // ── Filters ───────────────────────────────────────────────────
  const [grnInsNo,     setGrnInsNo]     = useState("");
  const [storeId,      setStoreId]      = useState("Select");
  const [fromDate,     setFromDate]     = useState("");
  const [toDate,       setToDate]       = useState("");
  const [projectNameId, setProjectNameId] = useState("Select");
  const [status,       setStatus]       = useState("Select");
  const [projectCodeId, setProjectCodeId] = useState("Select");
  const [supplierId,   setSupplierId]   = useState("Select");
  const [royaltyNo,    setRoyaltyNo]    = useState("");
  const [vehicleCode,  setVehicleCode]  = useState("");

  // ── Dropdowns ─────────────────────────────────────────────────
  const [stores,        setStores]        = useState([]);
  const [suppliers,     setSuppliers]     = useState([]);
  const [projectNames,  setProjectNames]  = useState([]);
  const [projectCodes,  setProjectCodes]  = useState([]);

  // ── Autocomplete ──────────────────────────────────────────────
  const [suggestions,      setSuggestions]      = useState([]);
  const [showSuggestions,  setShowSuggestions]  = useState(false);
  const autocompleteRef = useRef(null);

  // ── Main grid ─────────────────────────────────────────────────
  const [rows,       setRows]       = useState([]);
  const [page,       setPage]       = useState(1);
  const [pageSize]                  = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // ── Vehicle details panel ─────────────────────────────────────
  const [showDetails,  setShowDetails]  = useState(false);
  const [detailGrnInsNo, setDetailGrnInsNo] = useState("");
  const [detailRows,   setDetailRows]   = useState([]);
  const [detailPage,   setDetailPage]   = useState(1);
  const [detailPageSize]                = useState(10);

  // ── Image modal ───────────────────────────────────────────────
  const [modalImage,  setModalImage]  = useState(null);
  const [showModal,   setShowModal]   = useState(false);

  const showingFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo   = Math.min(page * pageSize, totalCount);

  // Paged detail rows
  const pagedDetails = useMemo(() => {
    const start = (detailPage - 1) * detailPageSize;
    return detailRows.slice(start, start + detailPageSize);
  }, [detailRows, detailPage, detailPageSize]);

  const detailTotalPages = Math.ceil(detailRows.length / detailPageSize) || 1;

  // ── Status badge ──────────────────────────────────────────────
  const StatusBadge = ({ status: st }) => {
    // Confirmed → green (blinktext1), others → amber (blinktext)
    const isConfirmed = st === "Confirmed";
    const isClosed    = st === "Closed";
    const isCanceled  = st === "Canceled";
    const base = "px-2 py-0.5 rounded-lg text-xs font-bold inline-block";
    if (isConfirmed)
      return <span className={`${base} bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300`}>{st}</span>;
    if (isClosed)
      return <span className={`${base} bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300`}>{st}</span>;
    if (isCanceled)
      return <span className={`${base} bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`}>{st}</span>;
    // Not Confirmed
    return <span className={`${base} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`}>{st}</span>;
  };

  // ── Pagination Bar ────────────────────────────────────────────
  const PaginationBar = ({ pg, totalPg, total, pgSize, onPageChange, label }) => {
    const from = total === 0 ? 0 : (pg - 1) * pgSize + 1;
    const to   = Math.min(pg * pgSize, total);
    return (
      <div className="flex flex-col md:flex-row items-center justify-between mt-3 gap-3 px-4 pb-3">
        <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          {label}: <b>{from}</b> – <b>{to}</b> of <b>{total}</b>
        </div>
        {totalPg > 1 && (
          <div className="inline-flex rounded-xl overflow-hidden border border-gray-300 dark:border-gray-700">
            <button
              onClick={() => onPageChange(Math.max(1, pg - 1))}
              disabled={pg === 1}
              className={`px-3 py-1.5 text-xs font-bold ${pg === 1 ? "text-gray-400 cursor-not-allowed" : isDark ? "text-white hover:bg-white/10" : "text-gray-800 hover:bg-gray-100"}`}
            >
              <ChevronLeft className="w-3 h-3" />
            </button>
            {[...Array(totalPg)].map((_, i) => (
              <button
                key={i}
                onClick={() => onPageChange(i + 1)}
                className={`px-3 py-1.5 text-xs font-bold transition ${pg === i + 1 ? `${primaryBtnClass} text-white` : isDark ? "text-white hover:bg-white/10" : "text-gray-800 hover:bg-gray-100"}`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => onPageChange(Math.min(totalPg, pg + 1))}
              disabled={pg === totalPg}
              className={`px-3 py-1.5 text-xs font-bold ${pg === totalPg ? "text-gray-400 cursor-not-allowed" : isDark ? "text-white hover:bg-white/10" : "text-gray-800 hover:bg-gray-100"}`}
            >
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Validations ───────────────────────────────────────────────
  // Equivalent to: Check_from_to() + checkDate()
  const validateDates = useCallback(() => {
    const today = todayISO();

    if (fromDate && fromDate > today) {
      toast.warn("Select from date lesser than current date");
      return false;
    }
    if (toDate && toDate > today) {
      toast.warn("Select to date lesser than current date");
      return false;
    }
    if (fromDate && toDate && toDate < fromDate) {
      toast.warn("Please select from date less than to date");
      return false;
    }
    // Financial year start check
    if (fromDate && finYearStart) {
      const fyStart = finYearStart.split("/");
      const fyISO   = `${fyStart[2]}-${fyStart[1]}-${fyStart[0]}`;
      if (fromDate < fyISO) {
        toast.warn(`You can select only Greater than or Equal to Financial Year Date  ${finYearStart}`);
        return false;
      }
    }
    return true;
  }, [fromDate, toDate, finYearStart]);

  // ── API: fetchMeta ────────────────────────────────────────────
  const fetchMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-ins-listing/meta`, {
        params: { finYearId, branchId, branchType },
      });
      if (res.data.success) {
        setIsSiteInventory(res.data.isSiteInventory);
        setFromDate(res.data.defaultFromDate || "");
        setToDate(res.data.defaultToDate || "");
      } else {
        toast.error(res.data.message || "Failed to load meta");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Meta load error");
    }
  }, [finYearId, branchId, branchType]);

  // ── API: fetchDropdowns ───────────────────────────────────────
  const fetchDropdowns = useCallback(async (pt = purType) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-ins-listing/dropdowns`, {
        params: { finYearId, branchId, branchType, purType: pt },
      });
      if (res.data.success) {
        setStores(res.data.stores       || []);
        setSuppliers(res.data.suppliers || []);
        setProjectNames(res.data.projectNames || []);
        setProjectCodes(res.data.projectCodes || []);
      } else {
        toast.error(res.data.message || "Failed to load dropdowns");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Dropdown load error");
    }
  }, [finYearId, branchId, branchType, purType]);

  // ── API: reload suppliers on radio change ─────────────────────
  // Equivalent to: rbtn_ho_CheckedChanged / rbtn_brch_CheckedChanged
  const reloadSuppliers = useCallback(async (pt) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-ins-listing/suppliers-by-type`, {
        params: { finYearId, branchId, purType: pt },
      });
      if (res.data.success) {
        setSuppliers(res.data.suppliers || []);
        setSupplierId("Select"); // reset supplier on radio change
      }
    } catch (e) {
      toast.error("Failed to reload suppliers");
    }
  }, [finYearId, branchId]);

  // ── API: fetchList ────────────────────────────────────────────
  const fetchList = useCallback(async (pg = 1) => {
    if (!validateDates()) return;

    try {
      setLoading(true);
      setShowDetails(false); // hide details panel on new search

      const res = await axios.get(`${BASE_URL}/api/inventory/grn-ins-listing/list`, {
        params: {
          finYearId,
          branchId,
          purType,
          supplierId:  supplierId  !== "Select" ? supplierId  : "",
          grnInsNo:    grnInsNo.trim(),
          fromDate:    fromDate || "",
          toDate:      toDate   || "",
          status:      status   !== "Select" ? status   : "",
          storeId:     storeId  !== "Select" ? storeId  : "",
          projectId:   projectNameId !== "Select" ? projectNameId : (projectCodeId !== "Select" ? projectCodeId : ""),
          royaltyNo:   royaltyNo.trim(),
          vehicleCode: vehicleCode.trim(),
          page:        pg,
          pageSize,
        },
      });

      if (res.data.success) {
        setRows(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || pg);
      } else {
        toast.error(res.data.message || "Search failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Search error");
    } finally {
      setLoading(false);
    }
  }, [
    validateDates, finYearId, branchId, purType,
    supplierId, grnInsNo, fromDate, toDate, status,
    storeId, projectNameId, projectCodeId, royaltyNo, vehicleCode, pageSize,
  ]);

  // ── Autocomplete ──────────────────────────────────────────────
  const fetchSuggestions = useCallback(async (prefix) => {
    if (!prefix || prefix.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-ins-listing/autocomplete`, {
        params: { finYearId, branchId, purType, prefix },
      });
      if (res.data.success) {
        setSuggestions(res.data.suggestions || []);
        setShowSuggestions((res.data.suggestions || []).length > 0);
      }
    } catch {
      // Silent fail for autocomplete
    }
  }, [finYearId, branchId, purType]);

  // ── Project Name ↔ Code link ──────────────────────────────────
  // Equivalent to: ddl_projectname_SelectedIndexChanged
  const handleProjectNameChange = useCallback(async (val) => {
    setProjectNameId(val);
    if (val === "Select") {
      await fetchDropdowns(); // restore full project codes
      return;
    }
    try {
      const res = await axios.get(
        `${BASE_URL}/api/inventory/grn-ins-listing/project-codes-by-name/${val}`,
        { params: { finYearId } }
      );
      if (res.data.success) setProjectCodes(res.data.projectCodes || []);
    } catch { /* silent */ }
  }, [finYearId, fetchDropdowns]);

  // Equivalent to: ddl_projectcode_SelectedIndexChanged
  const handleProjectCodeChange = useCallback(async (val) => {
    setProjectCodeId(val);
    if (val === "Select") {
      await fetchDropdowns(); // restore full project names
      return;
    }
    try {
      const res = await axios.get(
        `${BASE_URL}/api/inventory/grn-ins-listing/project-names-by-code/${val}`,
        { params: { finYearId } }
      );
      if (res.data.success) setProjectNames(res.data.projectNames || []);
    } catch { /* silent */ }
  }, [finYearId, fetchDropdowns]);

  // ── Radio change ──────────────────────────────────────────────
  const handleRadioChange = useCallback(async (newRad) => {
    setRad(newRad);
    const pt = newRad === "HO" ? "H" : "B";
    await reloadSuppliers(pt);
  }, [reloadSuppliers]);

  // ── VIEW_GRN click (GRN Ins No link) ─────────────────────────
  // Equivalent to: gv_grnlist_RowCommand → VIEW_GRN
  // Confirmed rows: link disabled
  // Simply store grnInsId in sessionStorage → Entry page fetchEditData() does the rest
const handleViewGRN = useCallback(async (row) => {
  if (row.fld_isconfirmed) return; // Confirmed → disabled

  try {
    // Fetch the PO list for this GRN Inspection (edit mode)
    const res = await axios.get(
      `${BASE_URL}/api/inventory/grn-ins-listing/grn/${row.fld_grninsid}/purchase-orders`,
      { params: { finYearId } }
    );

    if (!res.data?.success) {
      toast.error(res.data?.message || "Failed to load GRN data for edit");
      return;
    }

    // Store full payload so Entry page can load PO grid + items
    sessionStorage.setItem(
      "grnInsSelectionPayload",
      JSON.stringify({
        grnInsId:              row.fld_grninsid,
        grnId:                 row.fld_grnid,
        grn_type:              purType === "H" ? "HO" : "BRANCH",
        flag_grnupdt:          true,
        grnins_purchaseorder:  res.data.grnins_purchaseorder || [],
        // also keep any extra fields the SP returned
        ...res.data,
      })
    );

    navigate("/Inventory/GoodsReceiptNote_Ins_EntryGST");
  } catch (e) {
    toast.error(e?.response?.data?.message || "Error loading edit data");
  }
}, [purType, finYearId, navigate]);

  // ── GRNVEH_INS_DET click (View link) ─────────────────────────
  // Equivalent to: gv_grnlist_RowCommand → GRNVEH_INS_DET
  const handleViewDetails = useCallback(async (grnId) => {
    try {
      setLoadingDetails(true);
      setShowDetails(true);
      setDetailPage(1);

      const res = await axios.get(
        `${BASE_URL}/api/inventory/grn-ins-listing/grn/${grnId}/vehicle-details`,
        { params: { finYearId } }
      );
      if (res.data.success) {
        setDetailGrnInsNo(res.data.grnInsNo || "");
        setDetailRows(res.data.details || []);
      } else {
        setDetailRows([]);
        toast.error(res.data.message || "Failed to load details");
      }
    } catch (e) {
      setDetailRows([]);
      toast.error(e?.response?.data?.message || "Error loading details");
    } finally {
      setLoadingDetails(false);
    }
  }, [finYearId]);

  // ── Image modal ───────────────────────────────────────────────
  const openModal = (src) => {
    if (!src) return;
    setModalImage(src);
    setShowModal(true);
  };

  // ── Handlers ──────────────────────────────────────────────────
  const handleSearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    fetchList(1);
  };

  const handleReset = async () => {
    setGrnInsNo("");
    setStoreId("Select");
    setFromDate("");
    setToDate("");
    setProjectNameId("Select");
    setStatus("Select");
    setProjectCodeId("Select");
    setSupplierId("Select");
    setRoyaltyNo("");
    setVehicleCode("");
    setRows([]);
    setTotalCount(0);
    setTotalPages(1);
    setPage(1);
    setShowDetails(false);
    await fetchDropdowns();
    toast.info("Reset done");
  };

  // ── Close autocomplete on outside click ───────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Lifecycle ─────────────────────────────────────────────────
  useEffect(() => {
    fetchMeta();
    fetchDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-search on page change
  useEffect(() => {
    if (rows.length > 0 || page > 1) fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ── Render ─────────────────────────────────────────────────────
  return (
    <LayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      {/* ── Image Modal ── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
        >
          <div
            className="relative rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-2 right-2 z-10 bg-white/90 rounded-full p-1 shadow"
            >
              <X className="w-5 h-5 text-gray-800" />
            </button>
            <img
              src={modalImage}
              alt="Inspection"
              className="max-w-[480px] max-h-[480px] object-contain"
            />
          </div>
        </div>
      )}

      <div className={`min-h-screen p-4 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl">GRN INSPECTION LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-full p-5 mt-5 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* ── Top Bar ── */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <div className="text-xl font-extrabold">GRN Inspection Listing</div>
              <div className={`px-3 py-1 rounded-xl text-xs font-bold ${isDark ? "bg-blue-900/40 text-blue-200" : "bg-blue-100 text-blue-800"}`}>
                INFRA SCREEN : A-23
              </div>
              <div className={`px-3 py-1 rounded-xl text-xs font-bold ${isDark ? "bg-emerald-900/40 text-emerald-200" : "bg-emerald-100 text-emerald-800"}`}>
                {totalCount} Records
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-end">
              <button onClick={handleReset} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Reset
              </button>
              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
              {/* Add New — equivalent to ImgBtn_AddNew_Click */}
              <button
                onClick={() => navigate("/Inventory/GoodsReceiptNote_InsAdd")}
                className={primaryBtn}
              >
                <Plus className="w-4 h-4" />
                Add New
              </button>
            </div>
          </div>

          {/* ── Filters ── */}
          {showFilters && (
            <form onSubmit={handleSearch} className={`${cardBase} p-5 mb-5`}>

              {/* HO / Branch radio */}
              <div className="mb-4 flex items-center gap-6">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="branch"
                    checked={rad === "HO"}
                    onChange={() => handleRadioChange("HO")}
                    className="w-4 h-4"
                  />
                  <span className={isDark ? "text-gray-200 font-semibold" : "text-gray-700 font-semibold"}>H.O</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="branch"
                    checked={rad === "BRANCH"}
                    onChange={() => handleRadioChange("BRANCH")}
                    className="w-4 h-4"
                  />
                  <span className={isDark ? "text-gray-200 font-semibold" : "text-gray-700 font-semibold"}>Branch</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* GRN Inspection No (with autocomplete) */}
                <div ref={autocompleteRef} className="relative">
                  <label className={labelBase}>GRN Inspection No</label>
                  <input
                    type="text"
                    value={grnInsNo}
                    onChange={(e) => {
                      setGrnInsNo(e.target.value);
                      fetchSuggestions(e.target.value);
                    }}
                    onFocus={() => grnInsNo && fetchSuggestions(grnInsNo)}
                    placeholder="Type to search..."
                    className={inputBase}
                    autoComplete="off"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <ul className={`absolute z-20 w-full mt-1 rounded-xl border shadow-lg max-h-48 overflow-y-auto ${isDark ? "bg-gray-800 border-white/10" : "bg-white border-gray-200"}`}>
                      {suggestions.map((sg, i) => (
                        <li
                          key={i}
                          onClick={() => {
                            setGrnInsNo(sg);
                            setShowSuggestions(false);
                          }}
                          className={`px-4 py-2 text-sm cursor-pointer ${isDark ? "hover:bg-white/10 text-gray-200" : "hover:bg-gray-50 text-gray-800"}`}
                        >
                          {sg}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Store */}
                <div>
                  <label className={labelBase}>Store</label>
                  <select value={storeId} onChange={(e) => setStoreId(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {stores.map((st) => (
                      <option key={st.storeId} value={st.storeId}>{st.storeName}</option>
                    ))}
                  </select>
                </div>

                {/* From Date */}
                <div>
                  <label className={labelBase}>From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputBase}
                  />
                </div>

                {/* To Date */}
                <div>
                  <label className={labelBase}>To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputBase}
                  />
                </div>

                {/* Project Name */}
                <div>
                  <label className={labelBase}>Project Name</label>
                  <select
                    value={projectNameId}
                    onChange={(e) => handleProjectNameChange(e.target.value)}
                    className={inputBase}
                  >
                    <option value="Select">Select</option>
                    {projectNames.map((p) => (
                      <option key={p.projectId} value={p.projectId}>{p.projectName}</option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className={labelBase}>Status</label>
                  <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="Not Confirmed">Not Confirmed</option>
                  </select>
                </div>

                {/* Project Code */}
                <div>
                  <label className={labelBase}>Project Code</label>
                  <select
                    value={projectCodeId}
                    onChange={(e) => handleProjectCodeChange(e.target.value)}
                    className={inputBase}
                  >
                    <option value="Select">Select</option>
                    {projectCodes.map((p) => (
                      <option key={p.projectId} value={p.projectId}>{p.projectCode}</option>
                    ))}
                  </select>
                </div>

                {/* Supplier */}
                <div>
                  <label className={labelBase}>Supplier</label>
                  <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {suppliers.map((sp) => (
                      <option key={sp.supplierId} value={sp.supplierId}>{sp.supplierName}</option>
                    ))}
                  </select>
                </div>

                {/* Royalty Number */}
                <div>
                  <label className={labelBase}>Royalty Number</label>
                  <input
                    type="text"
                    value={royaltyNo}
                    onChange={(e) => setRoyaltyNo(e.target.value)}
                    placeholder="Royalty Number..."
                    className={inputBase}
                  />
                </div>

                {/* Vehicle Code */}
                <div>
                  <label className={labelBase}>Vehicle Code</label>
                  <input
                    type="text"
                    value={vehicleCode}
                    onChange={(e) => setVehicleCode(e.target.value)}
                    placeholder="Vehicle Code..."
                    className={inputBase}
                  />
                </div>
              </div>

              <div className="mt-5 flex justify-center">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </form>
          )}

          {/* ── Main Grid (gv_grnlist) ── */}
          <div className={`${cardBase} mb-5`}>
            <div className={`px-4 py-3 font-bold text-sm border-b ${isDark ? "bg-white/10 text-white border-white/10" : "bg-gray-50 text-gray-800 border-gray-200"}`}>
              GRN Inspection Records
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={primaryBtnClass}>
                  <tr>
                    <th className={thClass}>Slno</th>
                    <th className={thClass}>GRN Inspection No.</th>
                    <th className={thClass}>Date</th>
                    <th className={thClass}>Supplier</th>
                    <th className={thClass}>Status</th>
                    {isSiteInventory && <th className={thClass}>Details</th>}
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? "divide-white/10" : "divide-gray-100"}`}>
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, i) => (
                      <tr key={i} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: isSiteInventory ? 6 : 5 }).map((__, c) => (
                          <td key={c} className="px-3 py-3">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={isSiteInventory ? 6 : 5} className={`${tdClass} py-10 text-center`}>
                        No Records Found!
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => {
                      const isConfirmed = !!row.fld_isconfirmed;
                      return (
                        <tr
                          key={row.fld_grninsid}
                          className={`${idx % 2 === 0 ? (isDark ? "bg-white/5" : "bg-white") : (isDark ? "bg-white/10" : "bg-gray-50")} transition hover:${isDark ? "bg-white/15" : "bg-blue-50/50"}`}
                        >
                          <td className={`${tdClass} text-center`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          {/* GRN Inspection No — link disabled if Confirmed */}
                          {/* <td className={tdClass}>
                            {isConfirmed ? (
                              <span className="text-gray-400 cursor-not-allowed text-sm">
                                {row.fld_grninsno}
                              </span>
                            ) : (
                              <button
                                onClick={() => handleViewGRN(row)}
                                className="text-blue-500 hover:text-blue-700 underline text-sm font-semibold text-left"
                              >
                                {row.fld_grninsno}
                              </button>
                            )}
                          </td> */}

                          <td className={tdClass}>
                            
                             
                                {row.fld_grninsno}
                    
                            
                          </td>

                          <td className={tdClass}>{row.fld_date}</td>
                          <td className={tdClass}>{row.fld_suppliername}</td>

                          {/* Status badge */}
                          <td className={tdClass}>
                            <StatusBadge status={row.status} />
                          </td>

                          {/* Details (View) — site inventory only */}
                          {isSiteInventory && (
                            <td className={`${tdClass} text-center`}>
                              <button
                                onClick={() => handleViewDetails(row.fld_grnid)}
                                className="inline-flex items-center gap-1 text-xs text-emerald-500 hover:text-emerald-700 font-semibold underline"
                              >
                                <Eye className="w-3 h-3" />
                                View
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <PaginationBar
              pg={page}
              totalPg={totalPages}
              total={totalCount}
              pgSize={pageSize}
              onPageChange={setPage}
              label="Records"
            />
          </div>

          {/* ── Vehicle Inspection Details Panel (pnl_veh_inspdet) ── */}
          {showDetails && (
            <div className={`${cardBase} mb-5`}>
              <div className={`px-4 py-3 flex items-center justify-between border-b ${isDark ? "bg-white/10 text-white border-white/10" : "bg-gray-50 text-gray-800 border-gray-200"}`}>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-sm">Vehicle Inspection Details</span>
                  {detailGrnInsNo && (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${isDark ? "bg-blue-900/40 text-blue-200" : "bg-blue-100 text-blue-800"}`}>
                      GRN No: {detailGrnInsNo}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className={`p-1.5 rounded-lg transition ${isDark ? "hover:bg-white/10 text-gray-300" : "hover:bg-gray-200 text-gray-600"}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={primaryBtnClass}>
                    <tr>
                      <th className={thClass}>Slno</th>
                      <th className={thClass}>Loading Date</th>
                      <th className={thClass}>Rec Date</th>
                      <th className={thClass}>Vehicle Code</th>
                      <th className={thClass}>Material</th>
                      <th className={thClass}>Wt Source</th>
                      <th className={thClass}>Wt Site</th>
                      <th className={thClass}>Actual Wt (MT)</th>
                      <th className={thClass}>Rate</th>
                      <th className={thClass}>Amount</th>
                      <th className={thClass}>Site Name</th>
                      <th className={thClass}>Supplier</th>
                      <th className={thClass}>Royalty No</th>
                      <th className={thClass}>Vehicle Image</th>
                      <th className={thClass}>Veh &amp; Mat Image</th>
                      <th className={thClass}>Rej Item Image</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? "divide-white/10" : "divide-gray-100"}`}>
                    {loadingDetails ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 16 }).map((__, c) => (
                            <td key={c} className="px-3 py-3">
                              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : pagedDetails.length === 0 ? (
                      <tr>
                        <td colSpan={16} className={`${tdClass} py-8 text-center`}>
                          No Records Found!
                        </td>
                      </tr>
                    ) : (
                      pagedDetails.map((det, idx) => (
                        <tr
                          key={det.fld_inspectid ?? idx}
                          className={`${idx % 2 === 0 ? (isDark ? "bg-white/5" : "bg-white") : (isDark ? "bg-white/10" : "bg-gray-50")} transition`}
                        >
                          <td className={`${tdClass} text-center`}>
                            {(detailPage - 1) * detailPageSize + idx + 1}
                          </td>
                          <td className={tdClass}>{det.fld_loadingdate ? new Date(det.fld_loadingdate).toLocaleDateString("en-GB") : ""}</td>
                          <td className={tdClass}>{det.fld_Receivingdate ? new Date(det.fld_Receivingdate).toLocaleDateString("en-GB") : ""}</td>
                          <td className={tdClass}>{det.fld_vehcode}</td>
                          <td className={tdClass}>{det.fld_itemname}</td>
                          <td className={`${tdClass} text-center`}>{det.fld_Wtsource}</td>
                          <td className={`${tdClass} text-center`}>{det.fld_wtdestination}</td>
                          <td className={`${tdClass} text-center`}>{det.fld_actual}</td>
                          <td className={`${tdClass} text-center`}>{det.fld_poprice}</td>
                          <td className={`${tdClass} text-center`}>{det.fld_Value}</td>
                          <td className={tdClass}>{det.fld_Storename}</td>
                          <td className={tdClass}>{det.fld_suppliername}</td>
                          <td className={tdClass}>{det.fld_royaltynum}</td>

                          {/* Vehicle Image thumbnail → click to open modal */}
                          <td className={`${tdClass} text-center`}>
                            {det.vehicleImage ? (
                              <img
                                src={det.vehicleImage}
                                alt="Vehicle"
                                width={40}
                                height={30}
                                className="cursor-pointer rounded object-cover border border-gray-300 hover:opacity-80 transition"
                                onClick={() => openModal(det.vehicleImage)}
                              />
                            ) : <span className="text-gray-400 text-xs">—</span>}
                          </td>

                          {/* Vehicle & Material Image */}
                          <td className={`${tdClass} text-center`}>
                            {det.vehicleMatImage ? (
                              <img
                                src={det.vehicleMatImage}
                                alt="Veh Mat"
                                width={40}
                                height={30}
                                className="cursor-pointer rounded object-cover border border-gray-300 hover:opacity-80 transition"
                                onClick={() => openModal(det.vehicleMatImage)}
                              />
                            ) : <span className="text-gray-400 text-xs">—</span>}
                          </td>

                          {/* Rejected Item Image */}
                          <td className={`${tdClass} text-center`}>
                            {det.rejMatImage ? (
                              <img
                                src={det.rejMatImage}
                                alt="Rej Mat"
                                width={40}
                                height={30}
                                className="cursor-pointer rounded object-cover border border-gray-300 hover:opacity-80 transition"
                                onClick={() => openModal(det.rejMatImage)}
                              />
                            ) : <span className="text-gray-400 text-xs">—</span>}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <PaginationBar
                pg={detailPage}
                totalPg={detailTotalPages}
                total={detailRows.length}
                pgSize={detailPageSize}
                onPageChange={setDetailPage}
                label="Details"
              />
            </div>
          )}

        </div>
      </div>
    </LayoutWrapper>
  );
};

export default GoodsReceiptNoteInsListing;